import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { projectsApi } from '../../../services/api';
import type { ProjectInstance, StyleProfile } from './types';
import CreativeStyleSettings from '../CreativeStyleSettings';
import { PersonaPicker } from '../../PersonaPicker';
import CodeRepositoriesPanel from '../CodeRepositoriesPanel';
import { ExportProjectModal } from '../../ExportProjectModal';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
  Input,
  Label,
} from '@/components/ui';
import { logger } from '../../../utils/logger';

const personaNames: Record<string, string> = {
  'aerospace-engineer': 'Aerospace Engineer',
  'aerospace-avionics': 'Avionics Software Engineer',
  'fiction-writer': 'Fiction Writer',
  'fiction-mystery': 'Mystery Writer',
  'fiction-scifi': 'Science Fiction Writer',
  'technical-writer': 'Technical Writer',
  'phd-researcher': 'PhD Researcher',
};

interface SettingsTabProps {
  project: ProjectInstance;
  onEditProject?: () => void;
  onDeleteProject?: () => void;
}

export function SettingsTab({ project, onEditProject, onDeleteProject }: SettingsTabProps) {
  // Persona state
  const [showPersonaPicker, setShowPersonaPicker] = useState(false);
  const [projectPersonaId, setProjectPersonaId] = useState<string | undefined>(project.personaId);
  const [projectPersonaName, setProjectPersonaName] = useState<string>(
    project.personaId ? personaNames[project.personaId] || project.personaId : ''
  );
  
  useEffect(() => {
    setProjectPersonaId(project.personaId);
    setProjectPersonaName(project.personaId ? personaNames[project.personaId] || project.personaId : '');
  }, [project.personaId]);

  // Creative writing style profile state
  const [styleProfile, setStyleProfile] = useState<StyleProfile | undefined>(project.styleProfile);
  const [isSavingStyle, setIsSavingStyle] = useState(false);
  
  useEffect(() => {
    setStyleProfile(project.styleProfile);
  }, [project.styleProfile]);

  const handleStyleProfileChange = async (newStyle: StyleProfile) => {
    setStyleProfile(newStyle);
    setIsSavingStyle(true);
    try {
      await api.patch(`/projects/${project.id}`, { styleProfile: newStyle });
      logger.debug('Saved style profile for project:', newStyle);
    } catch (error) {
      logger.error('Failed to save style profile:', error);
    } finally {
      setIsSavingStyle(false);
    }
  };

  // Delete project state
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [deleteProjectConfirmText, setDeleteProjectConfirmText] = useState('');
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  const handleDeleteProject = async () => {
    if (deleteProjectConfirmText.toLowerCase() !== 'delete') return;
    
    setIsDeletingProject(true);
    try {
      await projectsApi.delete(project.id);
      setShowDeleteProjectModal(false);
      setDeleteProjectConfirmText('');
      if (onDeleteProject) {
        onDeleteProject();
      }
    } catch (err) {
      logger.error('Failed to delete project:', err);
    } finally {
      setIsDeletingProject(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Code Repositories Section - DO-178C Code Analysis */}
      <div className="bg-muted/30 rounded-lg p-4">
        <CodeRepositoriesPanel
          projectId={project.id}
          projectName={project.name}
        />
      </div>
      
      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          Project Settings
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Manage project metadata, team members, and configuration.
        </p>
        {onEditProject && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onEditProject}
          >
            Edit Project Settings
          </Button>
        )}
      </div>
      
      {/* Export PDF */}
      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          Export to PDF
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Generate a combined PDF of all project documents with bookmarks and table of contents.
          {project.projectType === 'creative' && ' Uses industry manuscript formatting.'}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowExportModal(true)}
          disabled={(project.documents?.length || 0) === 0}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
          </svg>
          Export Project PDF
        </Button>
      </div>
      
      {/* AI Persona Settings */}
      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          AI Assistant Persona
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Override the default AI persona for this project. This affects the AI's expertise, writing style, and available tools.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg">
              <span className="text-link">{projectPersonaId ? '✓' : '○'}</span>
              <span className="text-sm text-foreground">
                {projectPersonaName || 'Using your default persona'}
              </span>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowPersonaPicker(true)}
          >
            {projectPersonaId ? 'Change' : 'Set Persona'}
          </Button>
          {projectPersonaId && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={async () => {
                setProjectPersonaId(undefined);
                setProjectPersonaName('');
                try {
                  await api.patch(`/projects/${project.id}`, { personaId: null });
                  logger.debug('Cleared persona for project');
                } catch (error) {
                  logger.error('Failed to clear persona:', error);
                }
              }}
              title="Use default persona instead"
            >
              ✕ Clear
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {projectPersonaId 
            ? 'This project has a specific persona set. Click "Clear" to use your default persona instead.'
            : 'No override set. The AI will use your default persona from your profile settings.'}
        </p>
      </div>
      
      {/* Creative Writing Style Settings - only shown for creative projects */}
      {project.projectType === 'creative' && (
        <div className="bg-muted/30 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">
              Writing Style Settings
            </h3>
            {isSavingStyle && (
              <span className="text-xs text-muted-foreground">Saving...</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Configure the AI's writing style for this project. These settings are automatically applied to all creative writing sessions.
          </p>
          <CreativeStyleSettings
            styleProfile={styleProfile}
            onChange={handleStyleProfileChange}
            disabled={isSavingStyle}
          />
        </div>
      )}
      
      {/* Project ID for reference */}
      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-foreground">
          Project ID
        </h3>
        <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded break-all">
          {project.id}
        </code>
      </div>
      
      {/* Danger Zone - Delete Project */}
      <div className="bg-destructive-light border border-destructive rounded-lg p-4">
        <h3 className="text-sm font-semibold text-destructive mb-2">
          Danger Zone
        </h3>
        <p className="text-sm text-destructive mb-3">
          Permanently delete this project and all its documents. This action cannot be undone.
        </p>
        <Button
          onClick={() => setShowDeleteProjectModal(true)}
          variant="destructive"
          size="sm"
        >
          Delete Project
        </Button>
      </div>

      {/* Delete Project Modal */}
      <Dialog open={showDeleteProjectModal} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteProjectModal(false);
          setDeleteProjectConfirmText('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Project</DialogTitle>
            <DialogDescription>
              This action <strong>cannot be undone</strong> and will permanently remove all project data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-destructive-light border-l-4 border-destructive rounded">
              <p className="text-sm text-destructive font-medium mb-2">This will delete:</p>
              <ul className="text-sm text-destructive list-disc pl-5 space-y-1">
                <li>All {project.documents?.length || 0} documents</li>
                <li>All diagrams and flow diagrams</li>
                <li>All project settings and metadata</li>
              </ul>
            </div>
            
            <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-2">
              <div>
                <strong>Project:</strong> {project.name}
              </div>
              {project.projectCode && (
                <div>
                  <strong>Code:</strong> {project.projectCode}
                </div>
              )}
              {project.customer && (
                <div>
                  <strong>Customer:</strong> {project.customer}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="delete-project-confirm">Type "delete" to confirm</Label>
              <Input
                id="delete-project-confirm"
                value={deleteProjectConfirmText}
                onChange={(e) => setDeleteProjectConfirmText(e.target.value)}
                placeholder="delete"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteProjectModal(false);
                setDeleteProjectConfirmText('');
              }}
              variant="outline"
              disabled={isDeletingProject}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteProject}
              disabled={deleteProjectConfirmText.toLowerCase() !== 'delete' || isDeletingProject}
              variant="destructive"
            >
              {isDeletingProject ? 'Deleting...' : 'Delete Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* PersonaPicker Modal */}
      <PersonaPicker
        isOpen={showPersonaPicker}
        onClose={() => setShowPersonaPicker(false)}
        onSelect={async (selectedPersonaId) => {
          setProjectPersonaId(selectedPersonaId);
          setProjectPersonaName(personaNames[selectedPersonaId] || selectedPersonaId);
          try {
            await api.patch(`/projects/${project.id}`, { personaId: selectedPersonaId });
            logger.debug('Saved persona for project:', selectedPersonaId);
          } catch (error) {
            logger.error('Failed to save persona:', error);
          }
        }}
        currentPersonaId={projectPersonaId}
        title="Select Project AI Persona"
      />
      
      {/* Export Project Modal */}
      {showExportModal && (
        <ExportProjectModal
          project={project}
          onClose={() => setShowExportModal(false)}
          open={showExportModal}
        />
      )}
    </div>
  );
}
